// src/components/Tableros/Campo/GestionTablerosField.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  TextField,
  Typography,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Tabs,
  Tab,
  Stack,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Button,
  Paper,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  ColorLens as ColorLensIcon,
  TextFields as TextFieldsIcon,
  ViewColumn as ViewColumnIcon,
  Style as StyleIcon,
} from '@mui/icons-material';

// Panel de pestañas para la configuración
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Temas predefinidos (para eventos de campo)
const themes = {
  atletismo: {
    headerColor: '#2c0369',
    positionColor: '#4c516d',
    nameBackgroundColor: '#515e7b',
    markBackgroundColor: '#151515',
    tableBackgroundColor: '#1a1a1a',
    globalFontColor: '#ffffff',
    headerFontColor: '#ffffff',
    athleteNameColor: '#ffffff',
    teamNameColor: '#e0e0e0',
    resultColor: '#ffffff',
    successColor: '#1b5e20',
    failColor: '#b71c1c',
    passColor: '#0d47a1',
  },
  indoor: {
    headerColor: '#1a237e',
    positionColor: '#303f9f',
    nameBackgroundColor: '#3949ab',
    markBackgroundColor: '#121212',
    tableBackgroundColor: '#0d0d0d',
    globalFontColor: '#ffffff',
    headerFontColor: '#ffffff',
    athleteNameColor: '#ffffff',
    teamNameColor: '#bbdefb',
    resultColor: '#ffeb3b',
    successColor: '#00c853',
    failColor: '#d50000',
    passColor: '#2962ff',
  },
  olímpico: {
    headerColor: '#00796b',
    positionColor: '#006064',
    nameBackgroundColor: '#00838f',
    markBackgroundColor: '#004d40',
    tableBackgroundColor: '#002419',
    globalFontColor: '#ffffff',
    headerFontColor: '#ffffff',
    athleteNameColor: '#ffffff',
    teamNameColor: '#b2dfdb',
    resultColor: '#ffd54f',
    successColor: '#00e676',
    failColor: '#ff1744',
    passColor: '#00b0ff',
  },
  claro: {
    headerColor: '#1565c0',
    positionColor: '#1976d2',
    nameBackgroundColor: '#1e88e5',
    markBackgroundColor: '#e3f2fd',
    tableBackgroundColor: '#ffffff',
    globalFontColor: '#212121',
    headerFontColor: '#ffffff',
    athleteNameColor: '#ffffff',
    teamNameColor: '#e1f5fe',
    resultColor: '#212121',
    successColor: '#2e7d32',
    failColor: '#c62828',
    passColor: '#0d47a1',
  },
};

const GestionTablerosField = ({ configurations, setConfigurations, openTablero, openWindows }) => {
  // Función para actualizar una configuración
  const handleEditChange = (id, field, value) => {
    const updatedConfigs = configurations.map((config) => {
      if (config.id === id) {
        if (field === 'name') {
          return {
            ...config,
            name: value,
          };
        } else if (field === 'eventType') {
          return {
            ...config,
            type: value === 'horizontal' ? 'field_horizontal' : 'field_vertical',
            settings: {
              ...config.settings,
              [field]: value,
            },
          };
        } else {
          return {
            ...config,
            settings: {
              ...config.settings,
              [field]: value,
            },
          };
        }
      }
      return config;
    });
    setConfigurations(updatedConfigs);
    localStorage.setItem('configurations', JSON.stringify(updatedConfigs));
  };

  // Función para eliminar una configuración
  const handleDelete = (id) => {
    const updatedConfigs = configurations.filter((config) => config.id !== id);
    setConfigurations(updatedConfigs);
    localStorage.setItem('configurations', JSON.stringify(updatedConfigs));
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Filtrar solo las configuraciones para eventos de campo
  const fieldConfigurations = configurations.filter(
    config => config.type === 'field_horizontal' || config.type === 'field_vertical' || config.type === 'field'
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        Gestión de Tableros de Campo
      </Typography>
      
      {fieldConfigurations.length === 0 ? (
        <Card sx={{ textAlign: 'center', p: 4, backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" color="textSecondary">
            No hay tableros de campo configurados
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Crea un nuevo tablero para eventos de campo en la pestaña "Ajustes"
          </Typography>
        </Card>
      ) : (
        <Stack spacing={3}>
          {fieldConfigurations.map((config) => (
            <ConfigCard
              key={config.id}
              config={config}
              handleEditChange={handleEditChange}
              handleDelete={handleDelete}
              openTablero={openTablero}
              openWindows={openWindows}
              configurations={configurations}
              isMobile={isMobile}
              isTablet={isTablet}
              themes={themes}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const ConfigCard = ({ 
  config, 
  handleEditChange, 
  handleDelete, 
  openTablero, 
  openWindows, 
  configurations,
  isMobile,
  isTablet,
  themes
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    const configToUpdate = configurations.find((c) => c.id === config.id);
    if (configToUpdate) {
      localStorage.setItem(`config-${config.id}`, JSON.stringify(configToUpdate));
      const tableroWindow = openWindows[config.id];
      if (tableroWindow && !tableroWindow.closed) {
        tableroWindow.postMessage({ type: 'updateConfig' }, '*');
      } else {
        alert('El tablero no está abierto o ha sido cerrado.');
      }
    }
  };

  // Función para aplicar un tema predefinido a una configuración
  const applyTheme = (themeName) => {
    const themeValues = themes[themeName];
    if (!themeValues) return;

    // Aplicar todos los valores del tema a la configuración
    Object.entries(themeValues).forEach(([key, value]) => {
      handleEditChange(config.id, key, value);
    });
  };

  const isHorizontal = config.settings?.eventType === 'horizontal' || config.type === 'field_horizontal';

  return (
    <Card 
      elevation={2} 
      sx={{ 
        overflow: 'visible',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <CardHeader
        title={
          <TextField
            value={config.name}
            onChange={(e) => handleEditChange(config.id, 'name', e.target.value)}
            variant="standard"
            fullWidth
            sx={{ 
              '& input': { 
                fontSize: '1.2rem', 
                fontWeight: 'bold',
              }
            }}
          />
        }
        action={
          <Stack direction="row" spacing={1}>
            <Tooltip title="Abrir tablero">
              <IconButton 
                color="primary" 
                onClick={() => openTablero(config.id)}
                sx={{ 
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                  }
                }}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Actualizar tablero abierto">
              <IconButton 
                color="info" 
                onClick={handleRefresh}
                sx={{ 
                  backgroundColor: 'rgba(3, 169, 244, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(3, 169, 244, 0.15)',
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar tablero">
              <IconButton 
                color="error" 
                onClick={() => handleDelete(config.id)}
                sx={{ 
                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.15)',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={expanded ? "Contraer" : "Expandir configuración"}>
              <IconButton
                onClick={() => setExpanded(!expanded)}
                sx={{ 
                  backgroundColor: expanded ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                  transition: 'transform 0.3s ease',
                  transform: expanded ? 'rotate(180deg)' : 'none',
                }}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        subheader={
          <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mt: 1 }}>
            <Chip
              icon={<ViewColumnIcon fontSize="small" />}
              label={isHorizontal ? "Horizontal" : "Vertical"}
              variant="outlined"
              color="primary"
              size="small"
            />
            <Chip
              icon={<VisibilityIcon fontSize="small" />}
              label={`${config.settings?.tableWidth || 0} × ${config.settings?.tableHeight || 0}`}
              variant="outlined"
              color="secondary"
              size="small"
            />
          </Stack>
        }
      />

      {expanded && (
        <>
          <Divider />
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons={isMobile ? "auto" : false}
              allowScrollButtonsMobile
              aria-label="configuración de tablero"
            >
              <Tab 
                icon={<SettingsIcon fontSize="small" />} 
                iconPosition="start" 
                label="Dimensiones" 
              />
              <Tab 
                icon={<ViewColumnIcon fontSize="small" />} 
                iconPosition="start" 
                label="Evento" 
              />
              <Tab 
                icon={<TextFieldsIcon fontSize="small" />} 
                iconPosition="start" 
                label="Tipografía" 
              />
              <Tab 
                icon={<ColorLensIcon fontSize="small" />} 
                iconPosition="start" 
                label="Colores" 
              />
              <Tab 
                icon={<StyleIcon fontSize="small" />} 
                iconPosition="start" 
                label="Temas" 
              />
            </Tabs>
          </Box>

          <CardContent>
            {/* Dimensiones */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Filas Visibles"
                    value={config.settings?.displayRows || 8}
                    onChange={(e) => handleEditChange(config.id, 'displayRows', parseInt(e.target.value) || 1)}
                    variant="outlined"
                    size="small"
                    type="number"
                    inputProps={{ min: 1 }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Ancho de Tabla (px)"
                    value={config.settings?.tableWidth || 1000}
                    onChange={(e) => handleEditChange(config.id, 'tableWidth', parseInt(e.target.value) || 0)}
                    variant="outlined"
                    size="small"
                    type="number"
                    inputProps={{ min: 100 }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Alto de Tabla (px)"
                    value={config.settings?.tableHeight || 800}
                    onChange={(e) => handleEditChange(config.id, 'tableHeight', parseInt(e.target.value) || 0)}
                    variant="outlined"
                    size="small"
                    type="number"
                    inputProps={{ min: 100 }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Evento */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardHeader title="Tipo de Evento" sx={{ pb: 0 }} />
                    <CardContent>
                      <RadioGroup
                        value={config.settings?.eventType || 'horizontal'}
                        onChange={(e) => handleEditChange(config.id, 'eventType', e.target.value)}
                        name="event-type-group"
                      >
                        <FormControlLabel 
                          value="horizontal" 
                          control={<Radio />} 
                          label="Horizontal (Lanzamiento, Salto Largo, Triple)" 
                        />
                        <FormControlLabel 
                          value="vertical" 
                          control={<Radio />} 
                          label="Vertical (Salto Alto, Salto con Pértiga)" 
                        />
                      </RadioGroup>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardHeader title="Opciones de Visualización" sx={{ pb: 0 }} />
                    <CardContent>
                      {isHorizontal ? (
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={config.settings?.showAttemptNumber || false}
                                onChange={(e) => handleEditChange(config.id, 'showAttemptNumber', e.target.checked)}
                              />
                            }
                            label="Mostrar Número de Intento"
                          />
                        </FormGroup>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          En eventos verticales (Salto Alto, Salto con Pértiga), los intentos se muestran siempre
                          como parte del resultado con los estados: Éxito (O), Fallo (X) y Pasa (P).
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tipografía */}
            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tamaño Fuente Encabezado"
                    type="number"
                    value={config.settings?.headerFontSize || 24}
                    onChange={(e) => handleEditChange(config.id, 'headerFontSize', parseInt(e.target.value))}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: <Typography variant="caption">px</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tamaño Fuente Nombre"
                    type="number"
                    value={config.settings?.nameFontSize || 36}
                    onChange={(e) => handleEditChange(config.id, 'nameFontSize', parseInt(e.target.value))}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: <Typography variant="caption">px</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tamaño Fuente Posición"
                    type="number"
                    value={config.settings?.positionFontSize || 48}
                    onChange={(e) => handleEditChange(config.id, 'positionFontSize', parseInt(e.target.value))}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: <Typography variant="caption">px</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tamaño Fuente Resultado"
                    type="number"
                    value={config.settings?.resultFontSize || 54}
                    onChange={(e) => handleEditChange(config.id, 'resultFontSize', parseInt(e.target.value))}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: <Typography variant="caption">px</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tamaño Fuente Equipo"
                    type="number"
                    value={config.settings?.teamFontSize || 24}
                    onChange={(e) => handleEditChange(config.id, 'teamFontSize', parseInt(e.target.value))}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: <Typography variant="caption">px</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tamaño Fuente Intentos"
                    type="number"
                    value={config.settings?.attemptFontSize || 32}
                    onChange={(e) => handleEditChange(config.id, 'attemptFontSize', parseInt(e.target.value))}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: <Typography variant="caption">px</Typography>,
                    }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Colores Básicos */}
            <TabPanel value={tabValue} index={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Fondo de la Tabla
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.tableBackgroundColor || '#1a1a1a',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.tableBackgroundColor || '#1a1a1a'}
                      onChange={(e) => handleEditChange(config.id, 'tableBackgroundColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Encabezado
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.headerColor || '#2c0369',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.headerColor || '#2c0369'}
                      onChange={(e) => handleEditChange(config.id, 'headerColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Posición
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.positionColor || '#4c516d',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.positionColor || '#4c516d'}
                      onChange={(e) => handleEditChange(config.id, 'positionColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Fondo Nombre
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.nameBackgroundColor || '#515e7b',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.nameBackgroundColor || '#515e7b'}
                      onChange={(e) => handleEditChange(config.id, 'nameBackgroundColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Fondo Marca
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.markBackgroundColor || '#151515',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.markBackgroundColor || '#151515'}
                      onChange={(e) => handleEditChange(config.id, 'markBackgroundColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Texto Encabezado
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.headerFontColor || '#ffffff',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.headerFontColor || '#ffffff'}
                      onChange={(e) => handleEditChange(config.id, 'headerFontColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Nombre Atleta
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.athleteNameColor || '#ffffff',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.athleteNameColor || '#ffffff'}
                      onChange={(e) => handleEditChange(config.id, 'athleteNameColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Nombre Equipo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.teamNameColor || '#e0e0e0',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.teamNameColor || '#e0e0e0'}
                      onChange={(e) => handleEditChange(config.id, 'teamNameColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color de Resultado
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        backgroundColor: config.settings?.resultColor || '#ffffff',
                      }}
                    />
                    <TextField
                      type="color"
                      value={config.settings?.resultColor || '#ffffff'}
                      onChange={(e) => handleEditChange(config.id, 'resultColor', e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Grid>
                
                {!isHorizontal && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" gutterBottom>
                        Color de Intento Exitoso
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            backgroundColor: config.settings?.successColor || '#1b5e20',
                          }}
                        />
                        <TextField
                          type="color"
                          value={config.settings?.successColor || '#1b5e20'}
                          onChange={(e) => handleEditChange(config.id, 'successColor', e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" gutterBottom>
                        Color de Intento Fallido
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            backgroundColor: config.settings?.failColor || '#b71c1c',
                          }}
                        />
                        <TextField
                          type="color"
                          value={config.settings?.failColor || '#b71c1c'}
                          onChange={(e) => handleEditChange(config.id, 'failColor', e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" gutterBottom>
                        Color de Intento Pasado
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            backgroundColor: config.settings?.passColor || '#0d47a1',
                          }}
                        />
                        <TextField
                          type="color"
                          value={config.settings?.passColor || '#0d47a1'}
                          onChange={(e) => handleEditChange(config.id, 'passColor', e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                        />
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </TabPanel>

            {/* Temas - Nueva Pestaña */}
            <TabPanel value={tabValue} index={4}>
              <Typography variant="subtitle1" gutterBottom>
                Aplicar Tema Predefinido
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Selecciona un tema para aplicar automáticamente todos sus colores a este tablero.
              </Typography>
              
              <Grid container spacing={2}>
                {Object.keys(themes).map((themeName) => (
                  <Grid item xs={6} sm={3} key={themeName}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => applyTheme(themeName)}
                      sx={{
                        backgroundColor: themes[themeName].headerColor,
                        color: themes[themeName].headerFontColor,
                        textTransform: 'capitalize',
                        height: '80px',
                        '&:hover': {
                          backgroundColor: themes[themeName].headerColor,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {themeName}
                    </Button>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Vista previa (colores actuales)
                </Typography>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    mt: 1, 
                    p: 0, 
                    backgroundColor: config.settings?.tableBackgroundColor || '#1a1a1a',
                    borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: config.settings?.headerColor || '#2c0369',
                      color: config.settings?.headerFontColor || '#ffffff',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {isHorizontal ? 'Lanzamiento de Peso' : 'Salto con Pértiga'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Box
                      sx={{
                        width: '15%',
                        minHeight: 80,
                        backgroundColor: config.settings?.positionColor || '#4c516d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: config.settings?.headerFontColor || '#ffffff',
                      }}
                    >
                      <Typography variant="h5" fontWeight="bold">1</Typography>
                    </Box>
                    
                    <Box
                      sx={{
                        width: '45%',
                        minHeight: 80,
                        backgroundColor: config.settings?.nameBackgroundColor || '#515e7b',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          color: config.settings?.athleteNameColor || '#ffffff',
                          fontWeight: 'bold',
                        }}
                      >
                        Atleta Ejemplo
                      </Typography>
                      <Typography
                        sx={{
                          color: config.settings?.teamNameColor || '#e0e0e0',
                          fontSize: '0.85em',
                        }}
                      >
                        Equipo Ejemplo
                      </Typography>
                    </Box>
                    
                    {isHorizontal ? (
                      <Box
                        sx={{
                          width: '40%',
                          minHeight: 80,
                          backgroundColor: config.settings?.markBackgroundColor || '#151515',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            color: config.settings?.resultColor || '#ffffff',
                            fontWeight: 'bold',
                            fontSize: '1.2em',
                          }}
                        >
                          7.25m
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '40%',
                          minHeight: 80,
                          display: 'flex',
                        }}
                      >
                        <Box
                          sx={{
                            width: '40%',
                            backgroundColor: config.settings?.markBackgroundColor || '#151515',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              color: config.settings?.resultColor || '#ffffff',
                              fontWeight: 'bold',
                            }}
                          >
                            1.70
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: '20%',
                            backgroundColor: config.settings?.failColor || '#b71c1c',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                          }}
                        >
                          X
                        </Box>
                        <Box
                          sx={{
                            width: '20%',
                            backgroundColor: config.settings?.passColor || '#0d47a1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                          }}
                        >
                          P
                        </Box>
                        <Box
                          sx={{
                            width: '20%',
                            backgroundColor: config.settings?.successColor || '#1b5e20',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                          }}
                        >
                          O
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            </TabPanel>
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default GestionTablerosField;